import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, ExternalLink, Quote } from "lucide-react";
import api from "../lib/api";

const Stars = ({ rating = 5, size = 14 }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        size={size}
        className={i < Math.round(rating) ? "text-yellow-500 fill-yellow-500" : "text-slate-300"}
        strokeWidth={1.5}
      />
    ))}
  </div>
);

const initials = (name = "") =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const GoogleGlyph = ({ size = 16 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <path fill="#4285F4" d="M22.5 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.07 5.07 0 0 1-2.2 3.33v2.76h3.56c2.08-1.92 3.22-4.74 3.22-8.1Z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.56-2.76c-.99.66-2.25 1.05-3.72 1.05-2.86 0-5.28-1.93-6.15-4.52H2.18v2.84A11 11 0 0 0 12 23Z" />
    <path fill="#FBBC05" d="M5.85 14.12A6.6 6.6 0 0 1 5.5 12c0-.74.13-1.45.35-2.12V7.04H2.18A11 11 0 0 0 1 12c0 1.77.42 3.44 1.18 4.96l3.67-2.84Z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.07.56 4.21 1.65l3.15-3.15C17.46 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.04l3.67 2.84C6.72 7.31 9.14 5.38 12 5.38Z" />
  </svg>
);

const GoogleReviews = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get("/google-reviews")
      .then((res) => { if (mounted) setData(res.data); })
      .catch(() => { if (mounted) setError(true); });
    return () => { mounted = false; };
  }, []);

  if (error || !data) {
    return null;
  }

  const { rating, total, place_url, reviews = [] } = data;

  return (
    <section className="section-pad bg-white" data-testid="google-reviews">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        {/* Header — summary */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.55 }}
          className="grid lg:grid-cols-2 gap-10 items-end mb-10"
        >
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Reviews</p>
            <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">
              Wat klanten over ons zeggen op Google.
            </h2>
            <p className="mt-5 text-slate-600 text-lg max-w-lg">
              Onafhankelijke beoordelingen van klanten die voor Helios Bright hebben gekozen voor hun thuisbatterij of zonnepanelen.
            </p>
          </div>

          {/* Summary card */}
          <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 to-brand-soft/30 p-7 lg:p-8 flex items-center gap-5" data-testid="google-summary">
            <div className="w-16 h-16 rounded-2xl bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
              <GoogleGlyph size={28} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-heading font-black text-3xl text-navy-900 tabular-nums" data-testid="google-rating">{Number(rating).toFixed(1)}</span>
                <Stars rating={rating} size={18} />
              </div>
              <div className="text-sm text-slate-600 mt-1" data-testid="google-total">
                Op basis van {total} Google reviews
              </div>
            </div>
            <a
              href={place_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-pink !text-sm !py-2.5 !px-4 whitespace-nowrap hidden md:inline-flex"
              data-testid="google-cta-summary"
            >
              Bekijk op Google <ExternalLink size={14} />
            </a>
          </div>
        </motion.div>

        {/* Review cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {reviews.slice(0, 6).map((r, i) => (
            <motion.article
              key={`${r.author}-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: i * 0.06 }}
              className="relative bg-white rounded-3xl border border-slate-200 p-6 hover:border-brand/30 hover:-translate-y-1 hover:shadow-[0_30px_60px_rgba(15,23,42,0.06)] transition-all duration-300 flex flex-col"
              data-testid={`google-review-${i}`}
            >
              <Quote className="absolute top-5 right-5 text-brand/15" size={36} />
              <div className="flex items-center gap-3 mb-4 relative">
                <div className="w-11 h-11 rounded-full bg-brand-soft text-brand-deep flex items-center justify-center font-heading font-black flex-shrink-0">
                  {initials(r.author)}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-navy-900 truncate">{r.author}</div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Stars rating={r.rating} size={12} />
                    {r.relative_time && <span>· {r.relative_time}</span>}
                  </div>
                </div>
              </div>
              <p className="text-slate-700 text-[14px] leading-relaxed relative flex-1">{r.text}</p>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-400">
                <GoogleGlyph size={12} />
                <span>Gepubliceerd op Google</span>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 text-center">
          <a
            href={place_url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-pink"
            data-testid="google-cta-bottom"
          >
            Bekijk alle reviews op Google <ExternalLink size={16} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
